import type { ReactNode } from 'react'
import { slugAuthRedirectTarget } from '../utils/org-slug'

/**
 * Invariant: an auth route never resolves under an org basename.
 *
 * `main.tsx` also redirects slug-prefixed auth URLs, but only once, at
 * bootstrap. That covers a full page load and nothing else. Because the router
 * `basename` is `/<orgSlug>`, an in-app navigation to `/login` — most commonly
 * `<Navigate to="/login">` from `ProtectedRoute` (App.tsx), the path every
 * logged-out user takes — resolves to `/<slug>/login` and mounts the auth page
 * there with no page load and no chance for the bootstrap guard to run.
 *
 * This guard sits at the route rather than at the ~11 navigation call sites, so
 * it fires however the route was reached, and a new call site cannot reintroduce
 * the defect.
 *
 * The redirect runs during render, not in an effect: an effect fires after paint,
 * which is precisely the flash of a wrongly-scoped auth page we are removing.
 * `location.replace` tears this tree down, so the impurity is bounded — and a
 * StrictMode double-invoke just replaces to the same URL twice.
 *
 * Slug-prefixed OAuth callbacks (`/<slug>/oauth/complete`, `/<slug>/oauth2/*`)
 * are deliberately NOT covered — they are legitimately org-scoped. The set lives
 * in `SLUG_AUTH_REDIRECT_SEGMENTS` (utils/org-slug.ts).
 */
export default function BareAuthOnly({ children }: { children: ReactNode }) {
  const bareTarget = slugAuthRedirectTarget()

  if (bareTarget !== null) {
    window.location.replace(bareTarget)
    return null
  }

  return <>{children}</>
}
